import { ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  Eye,
  MapPin,
  MoreHorizontal,
  Package,
  Pencil,
  Play,
  Plus,
  Ship,
  Trash2,
  Truck,
  User,
  Users,
  XCircle
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import TaskList from '../components/TaskList';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Job, JobPriority, Shipment as JobShipment, JobStatus, JobType } from '../types/job';
import { Address, ShipmentCreateInput, ShipmentType } from '../types/shipment';

type Role = { key: string; name: string; description: string };
type UserType = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };

const JobDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<UserType | null>(null);

  // Tab state management - get from URL params or default to "overview"
  const activeTab = searchParams.get('tab') || 'overview';

  // Status update modal state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<JobStatus>(JobStatus.DRAFT);
  const [completionNotes, setCompletionNotes] = useState('');

  // Create shipment modal state
  const [createShipmentModalOpen, setCreateShipmentModalOpen] = useState(false);
  const [createShipmentStep, setCreateShipmentStep] = useState(1);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [createShipmentFormData, setCreateShipmentFormData] = useState({
    bl_number: '',
    shipment_type: '' as ShipmentType | '',
    origin_address_id: 'none',
    destination_address_id: 'none',
    planned_departure_at: '',
    planned_arrival_at: '',
  });
  const [createShipmentError, setCreateShipmentError] = useState<string | null>(null);
  const [createShipmentSubmitting, setCreateShipmentSubmitting] = useState(false);

  // Product units selection state
  const [orderItems, setOrderItems] = useState<any[]>([]);
  const [availableInstances, setAvailableInstances] = useState<{[key: string]: any[]}>({});
  const [selectedInstances, setSelectedInstances] = useState<string[]>([]);

  const loadJob = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load job');
      }

      const data = await response.json();
      setJob(data.success ? data.data : null);
    } catch (err) {
      console.error('Error loading job:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadJob();
    // Check auth
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, [loadJob]);

  const getJobTypeIcon = (type: JobType | null) => {
    if (!type) return <Package className="h-5 w-5" />;

    switch (type) {
      case JobType.DELIVERY:
        return <Truck className="h-5 w-5" />;
      case JobType.PICKUP:
        return <Ship className="h-5 w-5" />;
      case JobType.INSTALLATION:
        return <Package className="h-5 w-5" />;
      case JobType.MAINTENANCE:
        return <MapPin className="h-5 w-5" />;
      case JobType.TRANSFER:
        return <Package className="h-5 w-5" />;
      default:
        return <Package className="h-5 w-5" />;
    }
  };

  const getStatusBadgeVariant = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return 'default' as const;
      case JobStatus.IN_PROGRESS:
        return 'warning' as const;
      case JobStatus.ASSIGNED:
        return 'secondary' as const;
      case JobStatus.CANCELLED:
        return 'destructive' as const;
      case JobStatus.ON_HOLD:
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getPriorityBadgeVariant = (priority: JobPriority) => {
    switch (priority) {
      case JobPriority.URGENT:
        return 'destructive' as const;
      case JobPriority.HIGH:
        return 'warning' as const;
      case JobPriority.NORMAL:
        return 'secondary' as const;
      case JobPriority.LOW:
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const canManageJobs = user?.roles?.some(role => ['admin', 'supervisor', 'dispatcher'].includes(role.key)) ?? false;
  const canUpdateStatus = canManageJobs || (user?.id === job?.assigned_driver_id);

  const getShipmentTypeIcon = (type: string) => {
    switch (type) {
      case 'inbound':
        return <Ship className="h-4 w-4" />;
      case 'outbound':
        return <Truck className="h-4 w-4" />;
      case 'transfer':
        return <Package className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const getShipmentTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'inbound':
        return 'default' as const;
      case 'outbound':
        return 'secondary' as const;
      case 'transfer':
        return 'warning' as const;
      default:
        return 'secondary' as const;
    }
  };

  const getShipmentStatus = (_shipment: JobShipment) => {
    // Derive shipment status from the current job's status
    if (!job) {
      return { status: 'Pending', variant: 'secondary' as const };
    }

    switch (job.status) {
      case JobStatus.COMPLETED:
        return { status: 'Delivered', variant: 'default' as const };
      case JobStatus.CANCELLED:
        return { status: 'Cancelled', variant: 'secondary' as const };
      case JobStatus.IN_PROGRESS:
        return { status: 'In Transit', variant: 'warning' as const };
      case JobStatus.ASSIGNED:
        return { status: 'Assigned', variant: 'secondary' as const };
      case JobStatus.ON_HOLD:
        return { status: 'On Hold', variant: 'warning' as const };
      default:
        return { status: 'Pending', variant: 'secondary' as const };
    }
  };

  const shipmentColumns: ColumnDef<JobShipment>[] = [
    {
      accessorKey: 'bl_number',
      header: 'BL Number',
      cell: ({ row }) => {
        const shipment = row.original;
        return (
          <button
            onClick={() => navigate(`/shipments/${shipment.id}`)}
            className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
          >
            {shipment.bl_number}
          </button>
        );
      },
    },
    {
      accessorKey: 'shipment_type',
      header: 'Type',
      cell: ({ row }) => {
        const shipment = row.original as any;
        return (
          <div className="flex items-center">
            {getShipmentTypeIcon(shipment.shipment_type)}
            <Badge variant={getShipmentTypeBadgeVariant(shipment.shipment_type)} className="ml-2">
              {shipment.shipment_type.charAt(0).toUpperCase() + shipment.shipment_type.slice(1)}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: 'origin_address',
      header: 'Origin',
      cell: ({ row }) => {
        const shipment = row.original as any;
        return shipment.origin_address ? (
          <div className="flex items-center text-sm">
            <MapPin className="h-3 w-3 mr-1 text-gray-400" />
            <span>{shipment.origin_address.city}, {shipment.origin_address.region}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">No origin</span>
        );
      },
    },
    {
      accessorKey: 'destination_address',
      header: 'Destination',
      cell: ({ row }) => {
        const shipment = row.original as any;
        return shipment.destination_address ? (
          <div className="flex items-center text-sm">
            <MapPin className="h-3 w-3 mr-1 text-gray-400" />
            <span>{shipment.destination_address.city}, {shipment.destination_address.region}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">No destination</span>
        );
      },
    },
    {
      accessorKey: 'asset_instances',
      header: 'Items',
      cell: ({ row }) => {
        const shipment = row.original as any;
        return (
          <div className="text-center">
            <span className="text-lg font-bold text-gray-900">
              {shipment.asset_instances?.length || 0}
            </span>
            <p className="text-xs text-gray-500">items</p>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const shipment = row.original;
        const { status, variant } = getShipmentStatus(shipment);
        return <Badge variant={variant}>{status}</Badge>;
      },
    },
    {
      accessorKey: 'planned_arrival_at',
      header: 'Planned Arrival',
      cell: ({ row }) => {
        const shipment = row.original;
        return shipment.planned_arrival_at ? (
          <div className="flex items-center text-sm">
            <Calendar className="h-3 w-3 mr-1 text-gray-400" />
            <span>{new Date(shipment.planned_arrival_at).toLocaleDateString()}</span>
          </div>
        ) : (
          <span className="text-gray-400 italic">Not set</span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      enableHiding: false,
      cell: ({ row }) => {
        const shipment = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/shipments/${shipment.id}`)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              {canManageJobs && (
                <>
                  <DropdownMenuItem onClick={() => navigate(`/shipments/${shipment.id}/edit`)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const handleStatusUpdate = async () => {
    if (!job) return;

    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let body = {};

      switch (newStatus) {
        case JobStatus.IN_PROGRESS:
          endpoint = `/api/jobs/${job.id}/start`;
          break;
        case JobStatus.COMPLETED:
          endpoint = `/api/jobs/${job.id}/complete`;
          body = { completion_notes: completionNotes };
          break;
        case JobStatus.CANCELLED:
          endpoint = `/api/jobs/${job.id}/cancel`;
          body = { reason: completionNotes };
          break;
        default:
          endpoint = `/api/jobs/${job.id}`;
          body = { status: newStatus };
      }

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to update job status');
      }

      setStatusModalOpen(false);
      setCompletionNotes('');
      loadJob(); // Reload job to show updated status
    } catch (err) {
      console.error('Error updating job status:', err);
    }
  };

  const handleDelete = async () => {
    if (!job || !window.confirm(`Are you sure you want to delete job ${job.job_number}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        navigate('/jobs');
      } else {
        alert(data.message || 'Failed to delete job');
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      alert('An error occurred while deleting the job');
    }
  };

  const openStatusModal = (status: JobStatus) => {
    setNewStatus(status);
    setCompletionNotes('');
    setStatusModalOpen(true);
  };

  const loadAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/addresses', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load addresses');
      }

      const data = await response.json();
      setAddresses(data.success ? data.data : []);
    } catch (err) {
      console.error('Error loading addresses:', err);
    }
  };

  const openCreateShipmentModal = async () => {
    setCreateShipmentFormData({
      bl_number: '',
      shipment_type: '',
      origin_address_id: 'none',
      destination_address_id: 'none',
      planned_departure_at: '',
      planned_arrival_at: '',
    });
    setCreateShipmentError(null);
    setCreateShipmentStep(1);
    setSelectedInstances([]);
    loadAddresses();

    // Load order items if job has an order
    if (job?.order_id) {
      await loadOrderItemsAndInstances(job.order_id);
    }

    setCreateShipmentModalOpen(true);
  };

  const loadOrderItemsAndInstances = async (orderId: string) => {
    try {
      const token = localStorage.getItem('token');

      // Fetch order items
      const itemsResponse = await fetch(`/api/orders/${orderId}/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!itemsResponse.ok) {
        throw new Error('Failed to load order items');
      }

      const itemsData = await itemsResponse.json();
      const items = itemsData.success ? itemsData.data : [];
      setOrderItems(items);

      // For each order item, fetch available asset instances
      const instancesMap: {[key: string]: any[]} = {};

      for (const item of items) {
                if (item.asset_template_id) {
          const instancesResponse = await fetch(
            `/api/asset-instances?template_id=${item.asset_template_id}&status=available&limit=10`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (instancesResponse.ok) {
            const instancesData = await instancesResponse.json();
            instancesMap[item.asset_template_id] = instancesData.success ? instancesData.data : [];
          }
        }
      }

      setAvailableInstances(instancesMap);
    } catch (err) {
      console.error('Error loading order items and instances:', err);
      setCreateShipmentError('Failed to load product information');
    }
  };

  const handleCreateShipmentFormChange = (field: string, value: string) => {
    setCreateShipmentFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateShipmentNext = () => {
    // Validate step 1 before proceeding
    if (!createShipmentFormData.bl_number.trim()) {
      setCreateShipmentError('BL Number is required');
      return;
    }

    if (!createShipmentFormData.shipment_type) {
      setCreateShipmentError('Shipment Type is required');
      return;
    }

    setCreateShipmentError(null);
    setCreateShipmentStep(2);
  };

  const handleCreateShipmentBack = () => {
    setCreateShipmentStep(1);
    setCreateShipmentError(null);
  };

  const toggleInstanceSelection = (instanceId: string) => {
    setSelectedInstances(prev =>
      prev.includes(instanceId)
        ? prev.filter(id => id !== instanceId)
        : [...prev, instanceId]
    );
  };

  const handleCreateShipmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedInstances.length === 0) {
      setCreateShipmentError('Please select at least one product unit');
      return;
    }

    setCreateShipmentSubmitting(true);
    setCreateShipmentError(null);

    try {
      const token = localStorage.getItem('token');
      const payload: Partial<ShipmentCreateInput> = {
        bl_number: createShipmentFormData.bl_number.trim(),
        shipment_type: createShipmentFormData.shipment_type as ShipmentType,
        origin_address_id: createShipmentFormData.origin_address_id === 'none' ? null : createShipmentFormData.origin_address_id,
        destination_address_id: createShipmentFormData.destination_address_id === 'none' ? null : createShipmentFormData.destination_address_id,
        planned_departure_at: createShipmentFormData.planned_departure_at ? new Date(createShipmentFormData.planned_departure_at) : null,
        planned_arrival_at: createShipmentFormData.planned_arrival_at ? new Date(createShipmentFormData.planned_arrival_at) : null,
      };

      // First, create the shipment
      const createResponse = await fetch('/api/shipments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const createData = await createResponse.json();

      if (!createResponse.ok) {
        throw new Error(createData.message || 'Failed to create shipment');
      }

      const shipmentId = createData.data.id;

      // Add selected asset instances to the shipment
      for (const instanceId of selectedInstances) {
        const addAssetResponse = await fetch(`/api/shipments/${shipmentId}/assets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            asset_instance_id: instanceId,
          }),
        });

        if (!addAssetResponse.ok) {
          console.error(`Failed to add asset instance ${instanceId} to shipment`);
        }
      }

      // Then, link the shipment to this job
      const linkResponse = await fetch(`/api/jobs/${id}/shipments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          shipment_id: shipmentId,
          shipment_order: (job?.shipments?.length || 0) + 1,
        }),
      });

      const linkData = await linkResponse.json();

      if (!linkResponse.ok) {
        throw new Error(linkData.message || 'Failed to link shipment to job');
      }

      // Success - close modal and reload job
      setCreateShipmentModalOpen(false);
      loadJob();
    } catch (err: any) {
      setCreateShipmentError(err.message || 'Failed to create shipment');
    } finally {
      setCreateShipmentSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="jobs" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center">Loading job details...</div>
        </main>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="jobs" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center text-red-600">{error || 'Job not found'}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="jobs" />

      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')} className="h-8 w-8 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 min-w-0">
            {getJobTypeIcon(job.job_type)}
            <h1 className="text-2xl font-heading font-bold text-primary truncate">{job.job_number}</h1>
            <Badge variant={getStatusBadgeVariant(job.status)}>
              {job.status.replace('_', ' ').toUpperCase()}
            </Badge>
            <Badge variant={getPriorityBadgeVariant(job.priority)}>
              {job.priority.toUpperCase()}
            </Badge>
          </div>
          <div className="flex gap-2 ml-auto shrink-0">
            {canManageJobs && (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate(`/jobs/${id}/edit`)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDelete}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
            {canUpdateStatus && (
              <>
                {job.status === JobStatus.ASSIGNED && (
                  <Button onClick={() => openStatusModal(JobStatus.IN_PROGRESS)}>
                    <Play className="h-4 w-4 mr-2" />
                    Start Job
                  </Button>
                )}
                {job.status === JobStatus.IN_PROGRESS && (
                  <Button onClick={() => openStatusModal(JobStatus.COMPLETED)}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Complete Job
                  </Button>
                )}
                {canManageJobs && job.status !== JobStatus.COMPLETED && job.status !== JobStatus.CANCELLED && (
                  <Button variant="outline" onClick={() => openStatusModal(JobStatus.CANCELLED)}>
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Job
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(value) => setSearchParams({ tab: value })}
          className="space-y-6"
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="tasks">
              Tasks ({job.tasks?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="shipments">
              Shipments ({job.shipments?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-primary">
                    {job.job_type ? job.job_type.toUpperCase() : 'TASK-BASED'}
                  </div>
                  <div className="text-sm text-muted-foreground">Job Type</div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-primary">{job.priority.toUpperCase()}</div>
                  <div className="text-sm text-muted-foreground">Priority</div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-primary">
                    {job.estimated_duration_hours ? `${job.estimated_duration_hours}h` : 'N/A'}
                  </div>
                  <div className="text-sm text-muted-foreground">Estimated Duration</div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-primary">
                    {job.created_by_user?.first_name} {job.created_by_user?.last_name}
                  </div>
                  <div className="text-sm text-muted-foreground">Created By</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Details */}
              <div className="lg:col-span-3 space-y-6">
                {/* Order Information */}
                {job.order && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Order Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Order Number:</span>
                        <Link
                          to={`/orders/${job.order.id}`}
                          className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
                        >
                          {job.order.order_number}
                        </Link>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Customer:</span>
                        <span>{job.order.customer_name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Order Status:</span>
                        <Badge>{job.order.status.replace('_', ' ').toUpperCase()}</Badge>
                      </div>
                      {job.order.required_by_date && (
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Required By:</span>
                          <span>{new Date(job.order.required_by_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Job Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Job Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Job Code:</span>
                    <span>{job.job_code || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Created:</span>
                    <span>{new Date(job.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Planned Start:</span>
                    <span>{job.planned_start_at ? new Date(job.planned_start_at).toLocaleString() : 'Not set'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Planned End:</span>
                    <span>{job.planned_end_at ? new Date(job.planned_end_at).toLocaleString() : 'Not set'}</span>
                  </div>
                  {job.actual_start_at && (
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Actual Start:</span>
                      <span className="text-green-600">{new Date(job.actual_start_at).toLocaleString()}</span>
                    </div>
                  )}
                  {job.actual_end_at && (
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Actual End:</span>
                      <span className="text-green-600">{new Date(job.actual_end_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {job.notes && (
                  <div>
                    <span className="font-medium">Job Notes:</span>
                    <p className="text-muted-foreground mt-1">{job.notes}</p>
                  </div>
                )}
                {job.completion_notes && (
                  <div>
                    <span className="font-medium">Completion Notes:</span>
                    <p className="text-muted-foreground mt-1">{job.completion_notes}</p>
                  </div>
                )}
                {!job.notes && !job.completion_notes && (
                  <p className="text-gray-500">No notes available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Assignment Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Assignment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="font-medium">Driver:</span>
                  <div className="mt-1">
                    {job.assigned_driver ? (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{job.assigned_driver.first_name} {job.assigned_driver.last_name}</span>
                      </div>
                    ) : (
                      <span className="text-gray-500">Unassigned</span>
                    )}
                  </div>
                </div>

                <div>
                  <span className="font-medium">Supervisor:</span>
                  <div className="mt-1">
                    {job.assigned_supervisor ? (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{job.assigned_supervisor.first_name} {job.assigned_supervisor.last_name}</span>
                      </div>
                    ) : (
                      <span className="text-gray-500">No supervisor assigned</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Driver Location */}
            {job.driver_location_lat && job.driver_location_lon && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Driver Location
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-medium">Coordinates:</span>
                      <br />
                      {job.driver_location_lat.toFixed(6)}, {job.driver_location_lon.toFixed(6)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Last updated: {job.driver_location_updated_at ? new Date(job.driver_location_updated_at).toLocaleString() : 'Never'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </TabsContent>

      {/* Tasks Tab */}
      <TabsContent value="tasks">
        <TaskList jobId={job.id} editable={canManageJobs} onTaskUpdate={loadJob} />
      </TabsContent>

      {/* Shipments Tab */}
      <TabsContent value="shipments">
        <div className="space-y-4">
          {/* Create Shipment Button */}
          {canManageJobs && (
            <div className="flex justify-end">
              <Button onClick={() => navigate('/shipments/new', { state: { preSelectedJobId: id } })}>
                <Plus className="h-4 w-4 mr-2" />
                Create Shipment
              </Button>
            </div>
          )}

          {job.shipments && job.shipments.length > 0 ? (
            <DataTable
              columns={shipmentColumns}
              data={job.shipments}
              searchKey="bl_number"
              searchPlaceholder="Search by BL number..."
              emptyMessage="No shipments found"
            />
          ) : (
            <Card>
              <CardContent className="text-center py-8 text-muted-foreground">
                No shipments associated with this job yet.
              </CardContent>
            </Card>
          )}
        </div>
      </TabsContent>
    </Tabs>
  </main>

      {/* Status Update Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Job Status</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">New Status</Label>
              <div className="text-sm text-muted-foreground mt-1">
                {newStatus.replace('_', ' ').toUpperCase()}
              </div>
            </div>

            {(newStatus === JobStatus.COMPLETED || newStatus === JobStatus.CANCELLED) && (
              <div className="space-y-2">
                <Label htmlFor="notes">
                  {newStatus === JobStatus.COMPLETED ? 'Completion Notes' : 'Cancellation Reason'}
                </Label>
                <Textarea
                  id="notes"
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder={`Enter ${newStatus === JobStatus.COMPLETED ? 'completion notes' : 'cancellation reason'}...`}
                  rows={3}
                />
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStatusModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleStatusUpdate}
                className="flex-1"
              >
                Update Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Shipment Modal */}
      <Dialog open={createShipmentModalOpen} onOpenChange={setCreateShipmentModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Create New Shipment for Job {job?.job_number} - Step {createShipmentStep} of 2
            </DialogTitle>
          </DialogHeader>

          {createShipmentError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {createShipmentError}
            </div>
          )}

          {createShipmentStep === 1 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bl_number">BL Number *</Label>
                  <Input
                    id="bl_number"
                    type="text"
                    value={createShipmentFormData.bl_number}
                    onChange={(e) => handleCreateShipmentFormChange('bl_number', e.target.value)}
                    placeholder="e.g., INB-2025-001"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="shipment_type">Shipment Type *</Label>
                  <Select
                    value={createShipmentFormData.shipment_type}
                    onValueChange={(value) => handleCreateShipmentFormChange('shipment_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ShipmentType.INBOUND}>Inbound</SelectItem>
                      <SelectItem value={ShipmentType.OUTBOUND}>Outbound</SelectItem>
                      <SelectItem value={ShipmentType.TRANSFER}>Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="origin_address">Origin Address</Label>
                  <Select
                    value={createShipmentFormData.origin_address_id}
                    onValueChange={(value) => handleCreateShipmentFormChange('origin_address_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select origin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No origin address</SelectItem>
                      {addresses.map((address) => (
                        <SelectItem key={address.id} value={address.id}>
                          {address.city}, {address.region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="destination_address">Destination Address</Label>
                  <Select
                    value={createShipmentFormData.destination_address_id}
                    onValueChange={(value) => handleCreateShipmentFormChange('destination_address_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No destination address</SelectItem>
                      {addresses.map((address) => (
                        <SelectItem key={address.id} value={address.id}>
                          {address.city}, {address.region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="planned_departure_at">Planned Departure</Label>
                  <Input
                    id="planned_departure_at"
                    type="datetime-local"
                    value={createShipmentFormData.planned_departure_at}
                    onChange={(e) => handleCreateShipmentFormChange('planned_departure_at', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="planned_arrival_at">Planned Arrival</Label>
                  <Input
                    id="planned_arrival_at"
                    type="datetime-local"
                    value={createShipmentFormData.planned_arrival_at}
                    onChange={(e) => handleCreateShipmentFormChange('planned_arrival_at', e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setCreateShipmentModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="button" onClick={handleCreateShipmentNext}>
                  Next: Select Units
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleCreateShipmentSubmit} className="space-y-4">
              <div>
                <h3 className="font-semibold mb-3">Select Product Units to Ship</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Choose the specific units/instances to include in this shipment
                </p>

                {orderItems.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    No order items found for this job
                  </p>
                ) : (
                  <div className="space-y-4">
                    {orderItems.map((item) => {
                      const instances = availableInstances[item.asset_template_id] || [];
                      const primaryImage = item.asset_template?.images?.find((img: any) => img.variant === 'web');
                      const imageUrl = primaryImage?.url || item.asset_template?.images?.[0]?.url;

                      return (
                        <Card key={item.id}>
                          <CardHeader className="pb-3">
                            <div className="flex items-center gap-3">
                              {imageUrl ? (
                                <div className="flex-shrink-0 w-16 h-16 bg-muted rounded overflow-hidden">
                                  <img
                                    src={imageUrl}
                                    alt={item.asset_template?.name || 'Product'}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ) : (
                                <div className="flex-shrink-0 w-16 h-16 bg-muted rounded flex items-center justify-center">
                                  <Package className="h-8 w-8 text-muted-foreground" />
                                </div>
                              )}
                              <div>
                                <CardTitle className="text-base">
                                  {item.asset_template?.name || 'Product'}
                                </CardTitle>
                                <p className="text-sm text-muted-foreground">
                                  {instances.length} available units
                                </p>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {instances.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                No available units for this product
                              </p>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {instances.map((instance: any) => (
                                  <div
                                    key={instance.id}
                                    className="flex items-center space-x-2 p-2 border rounded hover:bg-muted/50"
                                  >
                                    <Checkbox
                                      id={instance.id}
                                      checked={selectedInstances.includes(instance.id)}
                                      onCheckedChange={() => toggleInstanceSelection(instance.id)}
                                    />
                                    <label
                                      htmlFor={instance.id}
                                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                                    >
                                      {instance.instance_number}
                                      {instance.current_location && (
                                        <span className="text-xs text-muted-foreground ml-2">
                                          @ {instance.current_location.city}
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {selectedInstances.length} unit(s) selected
                </div>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCreateShipmentBack}
                    disabled={createShipmentSubmitting}
                  >
                    Back
                  </Button>
                  <Button type="submit" disabled={createShipmentSubmitting || selectedInstances.length === 0}>
                    {createShipmentSubmitting ? 'Creating...' : 'Create Shipment'}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetails;
